import React, { useContext, useState, useRef } from 'react';

import { Alert, Button, Col, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEqual, reject, uniq, without } from 'lodash';
import Papa from 'papaparse';
import Swal from 'sweetalert2';

import ActionDropDown from 'components/shared/ActionDropDown';
import Container from 'components/shared/Container';
import {
  DATA_SOURCE_ATTRIBUTE_EXPORT,
  PRODUCT_LIST_ATTRIBUTION_CSV_EXPORT,
} from 'lib/networking/endpoints';
import {
  EXPORT_SOURCE_TYPES,
  ProductListExportContext,
} from 'components/product_lists/product_export/ProductListExportContainer';
import GlobalAttributes from 'lib/enums/GlobalAttributes';
import { P0SyncAttributes } from 'lib/enums/P0Attributes';
import ProductListSelectedAttributeSplitRow from 'components/product_lists/product_export/ProductListSelectedAttributeSplitRow';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import usePost from 'lib/hooks/usePost';
import { taxonomyColumns } from 'lib/enums/TaxonomyLevel';

const DEFAULT_SELECTED_ATTRIBUTES = [
  GlobalAttributes.gtin,
  P0SyncAttributes.sku,
  P0SyncAttributes.name,
  P0SyncAttributes.brand_name,
  P0SyncAttributes.manufacturer_name,
  P0SyncAttributes.pack_size,
  P0SyncAttributes.vendor_category,
  P0SyncAttributes.vendor_subcategory,
  P0SyncAttributes.catch_or_variable_weight,
  P0SyncAttributes.storage_temperature,
  P0SyncAttributes.trade_item_key_words_comma_separated,
  GlobalAttributes.status,
  P0SyncAttributes.thumbnail,
];

const DEFAULT_SELECTED_TAXONOMIES = [
  taxonomyColumns.l4_category_name,
  taxonomyColumns.l3_category_name,
  taxonomyColumns.l2_category_name,
  taxonomyColumns.l1_category_name,
  taxonomyColumns.l0_category_name,
  taxonomyColumns.l4_category_display_name,
  taxonomyColumns.l3_category_display_name,
  taxonomyColumns.l2_category_display_name,
  taxonomyColumns.l1_category_display_name,
  taxonomyColumns.l0_category_display_name,
];

const DEFAULT_SCORES = ['fsa_score'];

// For nutritional columns display as a single selectable item
const NUTRITIONAL_COLUMNS = 'nutritional_columns';

const DEFAULT_SELECTED_OTHER_COLUMNS = ['vendor_name', 'cnd_id', 'core_11', 'tag_family_data'];

function ProductListSelectedAttributeSplitView() {
  const hasUserPermissions = useUserPermissions();
  const { selectedAttributes, setSelectedAttributes, exportSource } =
    useContext(ProductListExportContext);
  const [selectedCategories, setSelectedCategories] = useState(DEFAULT_SELECTED_TAXONOMIES);
  const [defaultAttributes, setDefaultAttributes] = useState(DEFAULT_SELECTED_ATTRIBUTES);
  const [selectedScores, setSelectedScores] = useState(DEFAULT_SCORES);
  const [defaultOtherColumns, setDefaultOtherColumns] = useState(DEFAULT_SELECTED_OTHER_COLUMNS);
  const [withNutritionalColumns, setWithNutritionalColumns] = useState(false);
  const [skuList, setSkuList] = useState([]);
  const [useSkuFilter, setUseSkuFilter] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [csvFilename, setCsvFilename] = useState('');
  const fileInputRef = useRef(null);

  const { postData: exportAttributeReportCSV } = usePost(
    PRODUCT_LIST_ATTRIBUTION_CSV_EXPORT,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while. 
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while trying to export the file',
      }),
  );

  const { postData: exportDataSourceAttributeCSV } = usePost(
    DATA_SOURCE_ATTRIBUTE_EXPORT,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while. 
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while trying to export the file',
      }),
  );

  const handleClick = attr => {
    const uniqKey = attr.id + '_' + attr.attributeName + '_' + attr.isGlobalAttribute;
    setSelectedAttributes(reject(selectedAttributes, obj => isEqual(obj.uniqKey, uniqKey)));
  };

  const handleDefaultAttributesClick = attr => {
    if (defaultAttributes.find(attri => attri === attr)) {
      setDefaultAttributes([...without(defaultAttributes, attr)]);
    } else {
      setDefaultAttributes([...uniq([...defaultAttributes, attr])]);
    }
  };

  const handleDefaultTaxonomyClick = taxoColumn => {
    if (selectedCategories.find(taxoCol => taxoCol === taxoColumn)) {
      setSelectedCategories([...without(selectedCategories, taxoColumn)]);
    } else {
      setSelectedCategories([...uniq([...selectedCategories, taxoColumn])]);
    }
  };

  const handleScoreClick = scoreColumn => {
    if (selectedScores.find(scoreCol => scoreCol === scoreColumn)) {
      setSelectedScores([...without(selectedScores, scoreColumn)]);
    } else {
      setSelectedScores([...uniq([...selectedScores, scoreColumn])]);
    }
  };

  const handleDefaultOtherColumnsClick = column => {
    if (column === NUTRITIONAL_COLUMNS) {
      setWithNutritionalColumns(!withNutritionalColumns);
      return;
    }

    if (defaultOtherColumns.find(col => col === column)) {
      setDefaultOtherColumns([...without(defaultOtherColumns, column)]);
    } else {
      setDefaultOtherColumns([...uniq([...defaultOtherColumns, column])]);
    }
  };

  const handleCSVUpload = event => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFilename(file.name);
    setCsvError('');
    setUseSkuFilter(false);

    const reader = new FileReader();
    reader.onload = e => {
      const csvData = e.target.result;
      Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          // Look for a column named "SKUs" or similar
          const headers = results.meta.fields;
          const skuColumn = headers.find(
            header => header.toLowerCase() === 'skus' || header.toLowerCase() === 'sku',
          );

          if (skuColumn) {
            // Extract unique SKUs
            const skus = results.data
              .map(row => row[skuColumn])
              .filter(sku => sku && sku.trim() !== '');

            setSkuList(skus);
            setUseSkuFilter(true);
            Swal.fire({
              icon: 'success',
              title: 'SKUs Loaded',
              text: `Successfully loaded ${skus.length} SKUs from the CSV file.`,
            });
          } else {
            setCsvError('No column named "SKUs" or "SKU" found in the CSV file.');
          }
        },
        error: error => {
          setCsvError(`Error parsing CSV: ${error.message}`);
        },
      });
    };
    reader.onerror = () => {
      setCsvError('Error reading file');
    };
    reader.readAsText(file);
  };

  const ExportFile = (propagateData, allAttributes) => {
    let propagatedAttributes = false;
    if (propagateData) {
      propagatedAttributes = true;
    }

    const exportPayload = {
      attributes: uniq(
        selectedAttributes.map(attr => attr.attributeName).concat(defaultAttributes),
      ),
      taxonomies: allAttributes ? DEFAULT_SELECTED_TAXONOMIES : selectedCategories,
      propagated_data: propagatedAttributes,
      scores: allAttributes ? DEFAULT_SCORES : selectedScores,
      other_columns: allAttributes ? DEFAULT_SELECTED_OTHER_COLUMNS : defaultOtherColumns,
      all_attributes: allAttributes,
      with_nutritional_columns: withNutritionalColumns,
    };

    // Add SKU filter if applicable
    if (useSkuFilter && skuList.length > 0) {
      exportPayload.filter_skus = skuList;
    }

    if (exportSource.type === EXPORT_SOURCE_TYPES.PRODUCT_LIST) {
      exportPayload.product_list_id = exportSource.id;
      exportAttributeReportCSV(exportPayload);
    } else if (exportSource.type === EXPORT_SOURCE_TYPES.DATA_SOURCE) {
      exportPayload.data_source_id = exportSource.id;
      exportDataSourceAttributeCSV(exportPayload);
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const resetFileUpload = () => {
    setCsvFilename('');
    setSkuList([]);
    setUseSkuFilter(false);
    setCsvError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Container className="mt-5">
      <Row>
        <Col className="d-flex justify-content-end">
          {(exportSource.type !== EXPORT_SOURCE_TYPES.DATA_SOURCE ||
            hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA])) && (
            <ActionDropDown
              submenuItems={[
                {
                  title: 'Export product specific data',
                  action: () => ExportFile(false, false),
                },
                {
                  title: 'Export with propagated data',
                  action: () => ExportFile(true, false),
                },
                {
                  title: 'Export all attributes with specific data',
                  action: () => ExportFile(true, true),
                },
                {
                  title: 'Export all attributes with propagated data',
                  action: () => ExportFile(true, true),
                },
              ]}
            />
          )}
        </Col>
      </Row>
      <Row className="mb-4 mr-2">
        <Col>
          {selectedCategories.map(attr => (
            <ProductListSelectedAttributeSplitRow
              key={attr}
              eventHandler={handleDefaultTaxonomyClick}
              colValue={attr}
              objectValue={attr}
              isSelected={selectedCategories.includes(attr)}
            />
          ))}
          {defaultOtherColumns.map(attr => (
            <ProductListSelectedAttributeSplitRow
              key={attr}
              eventHandler={handleDefaultOtherColumnsClick}
              colValue={attr}
              objectValue={attr}
              isSelected={defaultOtherColumns.includes(attr)}
            />
          ))}
          <ProductListSelectedAttributeSplitRow
            key={NUTRITIONAL_COLUMNS}
            eventHandler={handleDefaultOtherColumnsClick}
            colValue="Include Nutritional Columns"
            objectValue={NUTRITIONAL_COLUMNS}
            isSelected={withNutritionalColumns}
          />
          {defaultAttributes.map(attr => (
            <ProductListSelectedAttributeSplitRow
              key={attr}
              eventHandler={handleDefaultAttributesClick}
              colValue={attr}
              objectValue={attr}
              isSelected={defaultAttributes.includes(attr)}
            />
          ))}
          {selectedAttributes.map(attr => (
            <ProductListSelectedAttributeSplitRow
              key={attr}
              eventHandler={handleClick}
              colValue={attr.attributeName}
              objectValue={attr}
              isSelected={true}
            />
          ))}
          {selectedScores.map(attr => (
            <ProductListSelectedAttributeSplitRow
              key={attr}
              eventHandler={handleScoreClick}
              colValue={attr}
              objectValue={attr}
              isSelected={selectedScores.includes(attr)}
            />
          ))}
        </Col>
        {exportSource.type === EXPORT_SOURCE_TYPES.PRODUCT_LIST && (
          <Col xs={6}>
            <h4>Filter by SKUs</h4>
            <p>Upload a CSV file with a column named SKUs to filter the export</p>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleCSVUpload}
              accept=".csv"
              style={{ display: 'none' }}
            />

            <div className={css(styles.dropzoneBox)} onClick={triggerFileInput}>
              <div>
                <div>
                  <FontAwesomeIcon icon={faCloudUploadAlt} />
                </div>
                <span>{csvFilename || 'Drop a CSV file here or click to upload'}</span>
              </div>
            </div>

            {csvFilename && (
              <Button variant="link" onClick={resetFileUpload} className="mt-2 p-0">
                Remove file
              </Button>
            )}

            {csvError && (
              <Alert variant="danger" className="mt-2">
                {csvError}
              </Alert>
            )}

            {useSkuFilter && skuList.length > 0 && (
              <Alert variant="success" className="mt-2">
                {`${skuList.length} SKUs loaded from file.` +
                  ` Export will be filtered to include only these SKUs.`}
              </Alert>
            )}
          </Col>
        )}
      </Row>
    </Container>
  );
}

const styles = StyleSheet.create({
  dropzoneBox: {
    background: 'white',
    border: '2px dashed #999',
    cursor: 'pointer',
    minHeight: '150px',
    padding: '40px 0px',
    borderRadius: '0.25rem',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ProductListSelectedAttributeSplitView;
